import React from 'react';
import { ArrowRight, CheckCircle, Award, TrendingUp, Shield, Users, Clock, Target, Code, Wrench } from 'lucide-react';
import Layout from '../layouts/Layout';
import CTA from '../components/CTA';
import { Link } from 'react-router-dom';

const AboutPage = () => {
    const values = [
        {
            icon: <Shield className="w-8 h-8" />,
            title: "Excellence",
            description: "We maintain the highest standards in everything we do, from code quality to client communication."
        },
        {
            icon: <Users className="w-8 h-8" />,
            title: "Partnership",
            description: "We work as an extension of your team, building long-term relationships based on trust and results."
        },
        {
            icon: <Target className="w-8 h-8" />,
            title: "Innovation",
            description: "We stay current with technology trends, constantly learning and adapting to deliver modern solutions."
        },
        {
            icon: <TrendingUp className="w-8 h-8" />,
            title: "Growth",
            description: "We're committed to driving measurable business growth for our clients through technology."
        }
    ];

    const expertise = [
        {
            category: "Frontend Development",
            technologies: ["React", "Next.js", "TypeScript", "Tailwind CSS", "Vue.js", "Angular"],
            experience: "10+ years",
            icon: <Code className="w-6 h-6" />
        },
        {
            category: "Backend Development",
            technologies: ["Node.js", "PHP", "Python", "Express", "Laravel", "FastAPI"],
            experience: "10+ years",
            icon: <Shield className="w-6 h-6" />
        },
        {
            category: "WordPress Development",
            technologies: ["Custom Themes", "Plugin Development", "WooCommerce", "Gutenberg", "Headless WP"],
            experience: "10+ years",
            icon: <Code className="w-6 h-6" />
        },
        {
            category: "Mobile Development",
            technologies: ["React Native", "iOS", "Android", "Flutter", "Ionic", "Native Apps"],
            experience: "8+ years",
            icon: <Target className="w-6 h-6" />
        },
        {
            category: "Database & Infrastructure",
            technologies: ["MySQL", "PostgreSQL", "MongoDB", "AWS", "Docker", "Kubernetes"],
            experience: "10+ years",
            icon: <Shield className="w-6 h-6" />
        },
        {
            category: "Maintenance & Support",
            technologies: ["24/7 Monitoring", "Performance Optimization", "Security Updates", "Backup Solutions"],
            experience: "10+ years",
            icon: <Wrench className="w-6 h-6" />
        }
    ];

    const achievements = [
        {
            number: "10+",
            label: "Years Experience",
            description: "Decade of expertise in enterprise development"
        },
        {
            number: "100%",
            label: "Client Satisfaction",
            description: "Committed to exceeding expectations"
        },
        {
            number: "24/7",
            label: "Support Available",
            description: "Round-the-clock technical support"
        },
        {
            number: "99.9%",
            label: "System Uptime",
            description: "Reliable, enterprise-grade infrastructure"
        }
    ];

    return (
        <Layout
            title="About Us"
            description="Learn about KGC Innovations Ltd - 10+ years of enterprise web development experience. UK-based team specializing in React, Node.js, PHP, WordPress, and mobile development."
            keywords="about KGC Innovations, UK web development company, enterprise development team, React specialists, Node.js experts, WordPress development"
            path="/about"
        >
            <div className="min-h-screen bg-white">
            {/* Hero Section */}
            <section className="relative pt-32 pb-20 px-6 bg-gradient-to-br from-slate-50 to-slate-100">
                <div className="max-w-6xl mx-auto">
                    <div className="grid lg:grid-cols-2 gap-12 items-center">
                        <div className="opacity-0 animate-[fadeInUp_0.8s_ease-out_0.2s_forwards]">
                            <div className="inline-flex items-center px-4 py-2 bg-slate-900 text-white rounded-full text-sm font-medium mb-8 transform hover:scale-105 transition-transform">
                                <Award className="w-4 h-4 mr-2" />
                                Professional Excellence
                            </div>
                            
                            <h1 className="text-5xl lg:text-6xl font-bold text-slate-900 mb-8 leading-tight">
                                A Decade of
                                <span className="block text-slate-700 mt-2">Technical Expertise</span>
                            </h1>
                            
                            <p className="text-xl text-slate-600 mb-10 leading-relaxed">
                                KGC Innovations Ltd brings over 10 years of proven development experience to every project. 
                                We combine deep technical expertise with strategic business insight to deliver exceptional results.
                            </p>
                            
                            <div className="flex flex-col sm:flex-row gap-4">
                                <Link to="/contact" className="bg-slate-900 text-center text-white px-8 py-4 rounded-xl font-semibold hover:bg-slate-800 transition-all hover:shadow-lg hover:scale-105 flex items-center justify-center">
                                    Work With Us <ArrowRight className="ml-2 w-5 h-5" />
                                </Link>
                                <Link to="/portfolio" className="border-2 border-slate-200 text-center text-slate-700 px-8 py-4 rounded-xl font-semibold hover:border-slate-300 hover:bg-slate-50 transition-all hover:scale-105">
                                    View Our Work
                                </Link>
                            </div>
                        </div>
                        
                        <div className="relative opacity-0 animate-[fadeInUp_0.8s_ease-out_0.4s_forwards]">
                            <div className="bg-white rounded-3xl p-8 shadow-2xl border border-slate-200 hover:shadow-3xl transition-shadow">
                                <div className="grid grid-cols-2 gap-6">
                                    <div className="text-center transform hover:scale-105 transition-transform">
                                        <div className="text-3xl font-bold text-slate-900 mb-2">10+</div>
                                        <div className="text-slate-600 text-sm">Years Experience</div>
                                    </div>
                                    <div className="text-center transform hover:scale-105 transition-transform">
                                        <div className="text-3xl font-bold text-slate-900 mb-2">100%</div>
                                        <div className="text-slate-600 text-sm">Client Satisfaction</div>
                                    </div>
                                    <div className="text-center transform hover:scale-105 transition-transform">
                                        <div className="text-3xl font-bold text-slate-900 mb-2">24/7</div>
                                        <div className="text-slate-600 text-sm">Support Available</div>
                                    </div>
                                    <div className="text-center transform hover:scale-105 transition-transform">
                                        <div className="text-3xl font-bold text-slate-900 mb-2">99.9%</div>
                                        <div className="text-slate-600 text-sm">System Uptime</div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Our Story */}
            <section className="py-24 bg-white">
                <div className="max-w-6xl mx-auto px-6">
                    <div className="grid lg:grid-cols-2 gap-16 items-center mb-20">
                        <div className="opacity-0 animate-[fadeInUp_0.8s_ease-out_0.2s_forwards]">
                            <h2 className="text-4xl font-bold text-slate-900 mb-8">Our Story</h2>
                            <div className="space-y-6 text-lg text-slate-600 leading-relaxed">
                                <p>
                                    KGC Innovations Ltd was established to bring over a decade of proven development 
                                    experience directly to businesses seeking exceptional technology solutions. Our founder 
                                    has spent 10+ years mastering enterprise-grade development across multiple industries.
                                </p>
                                <p>
                                    We understand that modern businesses need reliable, scalable solutions that work 
                                    flawlessly from day one. Our approach combines battle-tested methodologies with 
                                    cutting-edge technologies to deliver results that drive real business growth.
                                </p>
                                <p>
                                    Whether you need a complex web application, WordPress development, mobile solutions, 
                                    or ongoing maintenance and support, we bring the expertise and dedication to ensure 
                                    your project succeeds.
                                </p>
                            </div>
                        </div>
                        
                        <div className="bg-slate-50 rounded-2xl p-8 border border-slate-200 opacity-0 animate-[fadeInUp_0.8s_ease-out_0.4s_forwards] hover:shadow-lg transition-shadow">
                            <h3 className="text-xl font-bold text-slate-900 mb-6">Why Choose KGC Innovations?</h3>
                            <div className="space-y-4">
                                {[
                                    "10+ years of proven development experience",
                                    "Deep expertise across modern technology stacks",
                                    "WordPress specialists with custom solutions",
                                    "Comprehensive maintenance and support services",
                                    "UK-based with strong business understanding",
                                    "Focus on measurable business outcomes"
                                ].map((point, index) => (
                                    <div key={index} className="flex items-center opacity-0 animate-[fadeInUp_0.6s_ease-out_forwards]" style={{animationDelay: `${0.6 + index * 0.1}s`}}>
                                        <CheckCircle className="w-5 h-5 text-green-600 mr-4 flex-shrink-0" />
                                        <span className="text-slate-700">{point}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Our Values */}
            <section className="py-24 bg-slate-50">
                <div className="max-w-6xl mx-auto px-6">
                    <div className="text-center mb-16">
                        <h2 className="text-4xl font-bold text-slate-900 mb-6 opacity-0 animate-[fadeInUp_0.8s_ease-out_0.2s_forwards]">Our Values</h2>
                        <p className="text-xl text-slate-600 max-w-3xl mx-auto opacity-0 animate-[fadeInUp_0.8s_ease-out_0.4s_forwards]">
                            The principles that guide everything we do
                        </p>
                    </div>
                    
                    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
                        {values.map((value, index) => (
                            <div key={index} className="bg-white rounded-2xl p-8 border border-slate-200 text-center hover:shadow-lg hover:scale-105 transition-all opacity-0 animate-[fadeInUp_0.8s_ease-out_forwards]" style={{animationDelay: `${0.6 + index * 0.1}s`}}>
                                <div className="w-16 h-16 bg-slate-900 rounded-xl flex items-center justify-center text-white mx-auto mb-6 hover:bg-slate-800 transition-colors">
                                    {value.icon}
                                </div>
                                <h3 className="text-xl font-bold text-slate-900 mb-4">{value.title}</h3>
                                <p className="text-slate-600 leading-relaxed">{value.description}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Technical Expertise */}
            <section className="py-24 bg-white">
                <div className="max-w-6xl mx-auto px-6">
                    <div className="text-center mb-16">
                        <h2 className="text-4xl font-bold text-slate-900 mb-6 opacity-0 animate-[fadeInUp_0.8s_ease-out_0.2s_forwards]">Technical Expertise</h2>
                        <p className="text-xl text-slate-600 max-w-3xl mx-auto opacity-0 animate-[fadeInUp_0.8s_ease-out_0.4s_forwards]">
                            Comprehensive mastery across the modern technology landscape
                        </p>
                    </div>
                    
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {expertise.map((area, index) => (
                            <div key={index} className="bg-slate-50 rounded-2xl p-8 border border-slate-200 hover:shadow-lg hover:scale-105 transition-all opacity-0 animate-[fadeInUp_0.8s_ease-out_forwards]" style={{animationDelay: `${0.6 + index * 0.1}s`}}>
                                <div className="flex items-center justify-between mb-6">
                                    <div className="flex items-center">
                                        <div className="w-8 h-8 bg-slate-900 rounded-lg flex items-center justify-center text-white mr-3">
                                            {area.icon}
                                        </div>
                                        <h3 className="text-lg font-bold text-slate-900">{area.category}</h3>
                                    </div>
                                    <span className="text-sm font-medium text-slate-600">{area.experience}</span>
                                </div>
                                
                                <div className="flex flex-wrap gap-2">
                                    {area.technologies.map((tech, techIndex) => (
                                        <span key={techIndex} className="px-3 py-1 bg-white text-slate-700 rounded-full text-sm border border-slate-200 hover:bg-slate-900 hover:text-white transition-colors">
                                            {tech}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Achievements */}
            <section className="py-24 bg-slate-50">
                <div className="max-w-6xl mx-auto px-6">
                    <div className="text-center mb-16">
                        <h2 className="text-4xl font-bold text-slate-900 mb-6 opacity-0 animate-[fadeInUp_0.8s_ease-out_0.2s_forwards]">Our Commitment</h2>
                        <p className="text-xl text-slate-600 max-w-3xl mx-auto opacity-0 animate-[fadeInUp_0.8s_ease-out_0.4s_forwards]">
                            The standards that reflect our dedication to excellence
                        </p>
                    </div>
                    
                    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
                        {achievements.map((achievement, index) => (
                            <div key={index} className="bg-white rounded-2xl p-8 border border-slate-200 text-center hover:shadow-lg hover:scale-105 transition-all opacity-0 animate-[fadeInUp_0.8s_ease-out_forwards]" style={{animationDelay: `${0.6 + index * 0.1}s`}}>
                                <div className="text-4xl font-bold text-slate-900 mb-2">{achievement.number}</div>
                                <div className="text-lg font-semibold text-slate-900 mb-2">{achievement.label}</div>
                                <div className="text-slate-600">{achievement.description}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Our Approach */}
            <section className="py-24 bg-white">
                <div className="max-w-6xl mx-auto px-6">
                    <div className="grid lg:grid-cols-2 gap-16 items-center">
                        <div className="opacity-0 animate-[fadeInUp_0.8s_ease-out_0.2s_forwards]">
                            <h2 className="text-4xl font-bold text-slate-900 mb-8">Our Approach</h2>
                            <div className="space-y-6 text-lg text-slate-600 leading-relaxed">
                                <p>
                                    We believe that great technology solutions come from understanding both the technical 
                                    requirements and the business context. Our approach combines proven methodologies 
                                    with innovative thinking to deliver results that exceed expectations.
                                </p>
                                <p>
                                    Every project begins with a thorough analysis of your needs, followed by careful 
                                    planning and transparent communication throughout the development process. We ensure 
                                    you're involved at every step while handling the technical complexities.
                                </p>
                                <p>
                                    From initial concept to ongoing maintenance, we provide comprehensive support 
                                    that keeps your systems running smoothly and your business growing.
                                </p>
                            </div>
                            
                            <div className="mt-8">
                                <Link to="/contact" className="bg-slate-900 text-center text-white px-8 py-4 rounded-xl font-semibold hover:bg-slate-800 transition-all hover:shadow-lg hover:scale-105 flex items-center">
                                    Start Your Project <ArrowRight className="ml-2 w-5 h-5" />
                                </Link>
                            </div>
                        </div>
                        
                        <div className="space-y-6 opacity-0 animate-[fadeInUp_0.8s_ease-out_0.4s_forwards]">
                            {/* <div className="bg-slate-50 rounded-2xl p-8 border border-slate-200 hover:shadow-lg transition-shadow">
                                <h3 className="text-xl font-bold text-slate-900 mb-4">Leadership</h3>
                                <div className="space-y-4">
                                    <div className="flex items-center">
                                        <div className="w-12 h-12 bg-slate-900 rounded-full flex items-center justify-center text-white font-bold mr-4">
                                            K
                                        </div>
                                        <div>
                                            <div className="font-semibold text-slate-900">Kevin Crane</div>
                                            <div className="text-slate-600 text-sm">Founder & Lead Developer</div>
                                        </div>
                                    </div>
                                </div>
                            </div> */}
                            
                            <div className="bg-slate-900 text-white rounded-2xl p-8 hover:bg-slate-800 transition-colors">
                                <h3 className="text-xl font-bold mb-4">Our Promise</h3>
                                <div className="space-y-4">
                                    <div className="flex items-center">
                                        <Shield className="w-5 h-5 mr-3" />
                                        <span>Enterprise-grade security in every solution</span>
                                    </div>
                                    <div className="flex items-center">
                                        <Clock className="w-5 h-5 mr-3" />
                                        <span>On-time delivery with transparent reporting</span>
                                    </div>
                                    <div className="flex items-center">
                                        <Wrench className="w-5 h-5 mr-3" />
                                        <span>Comprehensive maintenance and support</span>
                                    </div>
                                    <div className="flex items-center">
                                        <TrendingUp className="w-5 h-5 mr-3" />
                                        <span>Focus on measurable business outcomes</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <CTA />

            <style jsx>{`
                @keyframes fadeInUp {
                    from {
                        opacity: 0;
                        transform: translateY(30px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }
                
                .animate-[fadeInUp_0.8s_ease-out_0.2s_forwards] {
                    animation: fadeInUp 0.8s ease-out 0.2s forwards;
                }
                
                .animate-[fadeInUp_0.8s_ease-out_0.4s_forwards] {
                    animation: fadeInUp 0.8s ease-out 0.4s forwards;
                }
                
                .animate-[fadeInUp_0.8s_ease-out_forwards] {
                    animation: fadeInUp 0.8s ease-out forwards;
                }
                
                .animate-[fadeInUp_0.6s_ease-out_forwards] {
                    animation: fadeInUp 0.6s ease-out forwards;
                }
            `}</style>
            </div>
        </Layout>
    );
};

export default AboutPage;